import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Alert, AlertDescription } from "../ui/alert";
import {
  Mail,
  Lock,
  Eye,
  EyeOff,
  Loader2,
  Heart,
  Shield,
  Users,
} from "lucide-react";
import { useAuth } from "../auth/AuthContext";
import { toast } from "sonner";
import axios from "axios";
import http from "@/lib/http";

// Zod validation schema
const loginSchema = z.object({
  email: z
    .string()
    .min(1, "Email is required")
    .email("Please enter a valid email address"),
  password: z
    .string()
    .min(1, "Password is required")
    .min(6, "Password must be at least 6 characters"),
  clinicId: z.string().min(1, "Please select a clinic"),
});

const Login = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [clinics, setClinics] = useState([]);
  const [loadingClinics, setLoadingClinics] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const form = useForm({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: "",
      password: "",
      clinicId: "",
    },
  });

  // Function to fetch clinics when email is valid
  const fetchClinics = async (email) => {
    if (!email || !email.includes("@")) return;

    setLoadingClinics(true);
    try {
      const response = await http.get(`/therapists/find-clinics/${email}`);
      setClinics(response.data || []);
    } catch (error) {
      console.error("Error fetching clinics:", error);
      setClinics([]);
    } finally {
      setLoadingClinics(false);
    }
  };

  // Watch email field for changes
  const watchedEmail = form.watch("email");

  // Fetch clinics when email changes and is valid
  React.useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (watchedEmail && watchedEmail.includes("@")) {
        fetchClinics(watchedEmail);
      } else {
        setClinics([]);
        form.setValue("clinicId", "");
      }
    }, 500); // Debounce for 500ms

    return () => clearTimeout(timeoutId);
  }, [watchedEmail]);

  const onSubmit = async (data) => {
    setLoading(true);
    setError("");

    try {
      const result = await login(data.email, data.password, data.clinicId);
      if (result.success) {
        navigate("/dashboard");
        toast.success("Welcome back!");
      } else {
        setError(result.error || "Login failed. Please try again.");
        toast.error(result.error || "Login failed");
      }
    } catch (err) {
      setError("An unexpected error occurred. Please try again.");
      toast.error("An unexpected error occurred");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="w-full max-w-6xl grid lg:grid-cols-2 gap-8 items-center">
        {/* Left side - Branding */}
        <div className="hidden lg:flex flex-col items-center justify-center text-center space-y-8">
          <div className="space-y-4">
            <div className="flex items-center justify-center space-x-3">
              <img
                src="/icon-large.png"
                alt="BookaTherapy"
                className="h-20 w-20 object-contain"
              />
            </div>
            <p className="text-xl text-slate-600 max-w-md">
              Empowering therapists with intelligent scheduling and patient
              management
            </p>
          </div>

          <div className="grid grid-cols-1 gap-6 max-w-md">
            <div className="flex items-center space-x-4 p-4 bg-white/50 backdrop-blur-sm rounded-xl border border-white/20">
              <div className="p-2 bg-indigo-100 rounded-lg">
                <Heart className="h-5 w-5 text-indigo-600" />
              </div>
              <div className="text-left">
                <h3 className="font-semibold text-slate-800">Patient Care</h3>
                <p className="text-sm text-slate-600">
                  Streamlined patient management
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-4 p-4 bg-white/50 backdrop-blur-sm rounded-xl border border-white/20">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Shield className="h-5 w-5 text-purple-600" />
              </div>
              <div className="text-left">
                <h3 className="font-semibold text-slate-800">
                  Secure Platform
                </h3>
                <p className="text-sm text-slate-600">
                  HIPAA compliant and secure
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-4 p-4 bg-white/50 backdrop-blur-sm rounded-xl border border-white/20">
              <div className="p-2 bg-green-100 rounded-lg">
                <Users className="h-5 w-5 text-green-600" />
              </div>
              <div className="text-left">
                <h3 className="font-semibold text-slate-800">
                  Team Collaboration
                </h3>
                <p className="text-sm text-slate-600">
                  Seamless clinic coordination
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Right side - Login Form */}
        <div className="w-full max-w-md mx-auto">
          <Card className="border-0 shadow-2xl bg-white/80 backdrop-blur-sm">
            <CardHeader className="space-y-1 pb-8">
              <div className="flex items-center justify-center mb-6">
                <img
                  src="/logo-bat.png"
                  alt="BookaTherapy"
                  className="h-12 w-auto"
                />
              </div>
              <CardTitle className="text-2xl font-bold text-center text-slate-800">
                Welcome Back
              </CardTitle>
              <CardDescription className="text-center text-slate-600">
                Sign in to your therapist portal
              </CardDescription>
            </CardHeader>

            <CardContent className="space-y-6">
              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <Form {...form}>
                <form
                  onSubmit={form.handleSubmit(onSubmit)}
                  className="space-y-6">
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium text-slate-700">
                          Email Address
                        </FormLabel>
                        <FormControl>
                          <div className="relative">
                            <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                            <Input
                              {...field}
                              type="email"
                              placeholder="therapist@clinic.com"
                              className="pl-10 h-11 border-slate-200 focus:border-indigo-500 focus:ring-indigo-500"
                              disabled={loading}
                            />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="clinicId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium text-slate-700">
                          Select Clinic
                        </FormLabel>
                        <FormControl>
                          <Select
                            onValueChange={field.onChange}
                            value={field.value}
                            disabled={
                              loading || loadingClinics || clinics.length === 0
                            }>
                            <SelectTrigger className="text-start h-11 border-slate-200 focus:border-indigo-500 focus:ring-indigo-500">
                              <SelectValue
                                placeholder={
                                  loadingClinics
                                    ? "Loading clinics..."
                                    : clinics.length === 0
                                    ? "Enter a valid email to see clinics"
                                    : "Select your clinic"
                                }
                              />
                            </SelectTrigger>
                            <SelectContent className="max-w-sm">
                              {clinics.map((clinic) => (
                                <SelectItem
                                  key={clinic._id || clinic.id}
                                  value={clinic._id || clinic.id}>
                                  <div className="flex flex-col max-w-xs">
                                    <span className="font-medium truncate">
                                      {clinic.clinicName}
                                    </span>
                                    {clinic.address && (
                                      <span className="text-sm text-slate-500 truncate">
                                        {clinic.address}
                                      </span>
                                    )}
                                  </div>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="password"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium text-slate-700">
                          Password
                        </FormLabel>
                        <FormControl>
                          <div className="relative">
                            <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                            <Input
                              {...field}
                              type={showPassword ? "text" : "password"}
                              placeholder="Enter your password"
                              className="pl-10 pr-10 h-11 border-slate-200 focus:border-indigo-500 focus:ring-indigo-500"
                              disabled={loading}
                            />
                            <button
                              type="button"
                              onClick={() => setShowPassword(!showPassword)}
                              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                              disabled={loading}>
                              {showPassword ? (
                                <EyeOff className="h-4 w-4" />
                              ) : (
                                <Eye className="h-4 w-4" />
                              )}
                            </button>
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="remember"
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-slate-300 rounded"
                      />
                      <Label
                        htmlFor="remember"
                        className="text-sm text-slate-600">
                        Remember me
                      </Label>
                    </div>
                    <button
                      type="button"
                      onClick={() => navigate("/forgot-password")}
                      className="text-sm text-indigo-600 hover:text-indigo-500 font-medium transition-colors">
                      Forgot password?
                    </button>
                  </div>

                  <Button
                    type="submit"
                    className="w-full h-11 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-medium transition-all duration-200 shadow-lg hover:shadow-xl"
                    disabled={loading}>
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Signing in...
                      </>
                    ) : (
                      "Sign In"
                    )}
                  </Button>
                </form>
              </Form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Login;
